import type { Building } from "building-types/building/buildingTypes";
import { WITNESS_LINE_GAP } from "building-types/constants/dimensionConstants";
import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { DimensionRowOutput, GeneratedDimensionMap } from "building-types/dimensions/dimensionTypes";
import { DimensionRowLevel } from "building-types/dimensions/dimensionTypesEnum";
import type { Doc } from "building-types/doc";
import {
  autoDimensionFloorPlanExteriorByRoomHandleMultipleNew,
  autoDimensionFloorPlanExteriorByOrientationHandleNew,
} from "building-types/handles/autoDimensionHandleTools";
import type { LevelHandle } from "building-types/handles/docEntityHandleTypes";
import type { HalfEdgeData } from "building-types/provenance/halfEdgeDataTypes";
import {
  AutoExteriorDimensionProvenanceCriteria,
  makeProvenanceCriteria,
} from "building-types/provenance/provenanceCriteriaTools";
import { ColumnDimensionType, ExteriorDimensionType } from "building-types/settings/settingsTypesEnum";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import { MIN_INTERNAL_PORCH_STRUCTURE_SIZE } from "common/constants/buildingObjects";
import { multimapAdd } from "common/data/multimap";
import { isCompositeLevelContentSourceId, resolveContentSourceIdToLevelIds } from "doc/docViewport";
import { docEntityHandleHash } from "doc/entity/core/handleHash";
import { edgeDataArrRemap } from "doc/provenance/edgeDataTools";
import {
  arrayUnionDistinct,
  collectDefinedHalfEdges,
  polygonHalfEdgesToPolygonEdgeData,
} from "doc/provenance/halfEdgeDataTools";
import { isExteriorRoomEnclosure, isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";
import {
  generatedExteriorDimensionNew,
  getMatchingEdgeOrientation,
  getRoomEdgesFromHalfEdgeData,
} from "doc/utils/dimensionTools";
import { ZERO_EPSILON } from "geom/core";
import { offsetPolyLine2 } from "geom/offset";
import { oppositeHalfEdgeIndex } from "geom/polyclip/dcelFromPolygons";
import { polygonUnion } from "geom/polygon";
import { polyLine2ReverseCopy, polyLine2CopySimplify, polyLine2Copy, polyLine2Area } from "geom/polyLine2";
import { vertexDataArrToEdgeDataAsArrayArr } from "geom/polyLine2Data";
import type { PolyLine2 } from "geom/polyLine2Types";
import { buildingIsEmpty } from "../building/buildingIsEmpty";
import { foundationPatioSlabs } from "../elements/foundation/foundationProfiles";
import { groundLevelId } from "../elements/level/levelTools";
import { extractPorchAnnotationInfo } from "../elements/porch/porchUtils";
import { getWallPath } from "../elements/walls/wall";
import { solidWallSubstrateLayer } from "../elements/walls/wallLayers";
import { makeExteriorDimensionRows } from "./dimensionRows";
import { makeExteriorWallSegments } from "./floorplan";

export interface MakeExteriorFloorplanDimensionsByLevelProps {
  readonly levelId: string;
  readonly building: Pick<
    Building,
    | "levels"
    | "planarGraphs"
    | "mirror"
    | "rooms"
    | "roomLevelIndices"
    | "roomsByLevel"
    | "settings"
    | "walls"
    | "floorPlates"
    | "dimensionBehavior"
    | "porches"
    | "foundations"
    | "foundationSuperSystems"
  > & { doc: Pick<Doc, "compositeLevels" | "levels"> };
}

export function makeExteriorFloorplanDimensionsByLevel({
  building,
  levelId,
}: MakeExteriorFloorplanDimensionsByLevelProps): GeneratedDimensionMap {
  if (buildingIsEmpty(building)) {
    return {};
  }

  const isComposite = isCompositeLevelContentSourceId(building.doc, levelId);
  const memberLevelIds = isComposite
    ? resolveContentSourceIdToLevelIds(building.doc, levelId, building.levels).filter((id) => building.levels[id])
    : [];

  if (isComposite && memberLevelIds.length === 0) {
    return {};
  }

  const {
    exteriorDimensionDatum,
    exteriorDimensionOffsetMultiplier,
    exteriorOpeningTagOffset,
    dimensionInteriorWallTJunctionsOnExteriorDims,
  } = building.settings.planset;
  const wallLayer =
    exteriorDimensionDatum === WallLateralDatumAnchor.OUTSIDE_FACE_OF_SUBSTRATE
      ? WallLayerType.SUBSTRATE
      : WallLayerType.STRUCTURE;
  const dimensionBehavior = building.dimensionBehavior[levelId];
  const excludeNonParallelWalls = !dimensionInteriorWallTJunctionsOnExteriorDims;

  const { exteriorWallSegments, exteriorWallHalfEdges: exteriorWallData } = makeExteriorWallSegments({
    building,
    levelId,
    includeDoors: false,
    includeWindows: false,
    includeExteriorRooms: true,
    wallLayer,
    dimensionBehavior,
    excludeNonParallelWalls,
  });

  const { interiorWallSegments } = makeExteriorWallSegments({
    building,
    levelId,
    includeDoors: false,
    includeWindows: false,
    includeExteriorRooms: false,
    wallLayer,
    dimensionBehavior,
    excludeNonParallelWalls,
  });

  exteriorWallSegments.push(...interiorWallSegments);
  exteriorWallData.push(...new Array(interiorWallSegments.length).fill(undefined));

  const { exteriorWallSegments: exteriorWallDetails, exteriorWallHalfEdges: exteriorWallDetailData } =
    makeExteriorWallSegments({
      building,
      levelId,
      includeDoors: true,
      includeWindows: true,
      includeExteriorRooms: true,
      wallLayer,
      dimensionBehavior,
      excludeUndimensionedOpenings: true,
      excludeNonParallelWalls,
    });

  const { interiorWallSegments: interiorWallDetails } = makeExteriorWallSegments({
    building,
    levelId,
    includeDoors: true,
    includeWindows: true,
    includeExteriorRooms: false,
    wallLayer,
    dimensionBehavior,
    excludeUndimensionedOpenings: true,
    excludeNonParallelWalls,
  });

  exteriorWallDetails.push(...interiorWallDetails);

  // combine exterior and empty interior half edge data to mirror the polyLine2 data
  const interiorWallDetailData = initializeEmptyWallDetailData(interiorWallSegments);
  exteriorWallDetailData.push(...interiorWallDetailData);

  const exteriorWallObstacles = isComposite
    ? memberLevelIds.flatMap((id) => makeExteriorDimensionObstacles(building, id))
    : makeExteriorDimensionObstacles(building, levelId);
  exteriorWallObstacles.push(...exteriorWallObstacles.map(polyLine2ReverseCopy));

  if (exteriorWallSegments.length === 0) {
    return {};
  }

  const { porches, levels, foundationSuperSystems } = building;
  const rowConfig = building.settings.planset.exteriorDimensionType;
  const baseLevelId = groundLevelId(levels);
  if (!baseLevelId) {
    return {};
  }

  const perimeters = exteriorWallSegments.map((x) => polyLine2CopySimplify(x));
  const exteriorWallDataByEdge = vertexDataArrToEdgeDataAsArrayArr(exteriorWallData);
  const perimeterWallDataByEdge = edgeDataArrRemap(
    exteriorWallSegments,
    perimeters,
    exteriorWallDataByEdge,
    arrayUnionDistinct,
    undefined,
  );
  const exteriorWallDetailDataByEdge = vertexDataArrToEdgeDataAsArrayArr(exteriorWallDetailData);
  const perimeterDetails = exteriorWallSegments.map(polyLine2Copy);
  const porchArray = Object.values(porches);
  const porchAnnotationLevelIds = isComposite ? memberLevelIds : [levelId];
  const colonnadePolyLines: PolyLine2[] = [];
  const colonnadeDataByEdge: (number | undefined)[][][] = [];
  const colonnadeInternalColumnStructureSizes: number[] = [];
  for (const porchLevelId of porchAnnotationLevelIds) {
    const {
      colonnadePolyLines: levelColonnadePolyLines,
      colonnadeEdgeDataArr: levelColonnadeDataByEdge,
      colonnadeInternalColumnStructureSizes: levelColonnadeInternalColumnStructureSizes,
    } = extractPorchAnnotationInfo(porchArray, porchLevelId);
    colonnadePolyLines.push(...levelColonnadePolyLines);
    colonnadeDataByEdge.push(...levelColonnadeDataByEdge);
    colonnadeInternalColumnStructureSizes.push(...levelColonnadeInternalColumnStructureSizes);
  }

  // Add patio segments
  const patioOutlines: PolyLine2[] = [];
  for (const foundation of Object.values(foundationSuperSystems)) {
    const matchesLevel = isComposite ? memberLevelIds.includes(foundation.levelId) : foundation.levelId === levelId;
    if (matchesLevel) {
      for (const slab of foundationPatioSlabs(foundation)) {
        patioOutlines.push(slab.planProfile[0]);
      }
    }
  }

  const porchObstacles: PolyLine2[] = [];

  // offset the colonnade lines in the opposite direction and flip them to use as
  // obstacles for windows/wall jogs inside the porch boundary
  const { columnDimensionType } = building.settings.planset;
  if (columnDimensionType !== ColumnDimensionType.NONE) {
    for (let i = 0; i < colonnadePolyLines.length; i++) {
      const internalColumnStructureSize = colonnadeInternalColumnStructureSizes[i] ?? 0;
      const pl = colonnadePolyLines[i];
      const obstacle = offsetPolyLine2(
        polyLine2ReverseCopy(pl),
        Math.max(internalColumnStructureSize, MIN_INTERNAL_PORCH_STRUCTURE_SIZE),
      );
      porchObstacles.push(obstacle);
      porchObstacles.push(polyLine2ReverseCopy(obstacle));
    }
  }

  // For composite levels only the outer shell of each unioned polygon is used; holes within
  // individual member footprints are intentionally discarded because other member levels may
  // fill them, and composite exterior dimensions are computed against the unified perimeter.
  const bounds: PolyLine2[] = isComposite
    ? polygonUnion(
        memberLevelIds.flatMap((id) => (building.floorPlates[id]?.perimeterPolyLine2s ?? []).map((pl) => [pl])),
      )
        .map((union) => union[0])
        .filter((loop): loop is PolyLine2 => !!loop && polyLine2Area(loop) > ZERO_EPSILON)
    : (building.floorPlates[levelId]?.perimeterPolyLine2s ?? []).map(polyLine2Copy);

  const mergeRows3And4 = rowConfig === ExteriorDimensionType.THREE_ROW;
  const includePorchObstaclesAndColonadeData = isComposite || building.levels[levelId].floorIndex === 0;

  const dims: DimensionRowOutput[] = [];

  if (includePorchObstaclesAndColonadeData) {
    makeExteriorDimensionRows({
      output: dims,
      input: [
        { level: DimensionRowLevel.BOUNDS, pls: bounds },
        { level: DimensionRowLevel.BOUNDS, pls: patioOutlines },
        {
          level: DimensionRowLevel.ROW_2,
          pls: perimeters,
          projectOnAxis: true,
          halfEdges: polygonHalfEdgesToPolygonEdgeData(levelId, perimeterWallDataByEdge),
        },
        {
          level: DimensionRowLevel.ROW_3,
          pls: perimeterDetails,
          halfEdges: polygonHalfEdgesToPolygonEdgeData(levelId, exteriorWallDataByEdge),
        },
        columnDimensionType === ColumnDimensionType.NONE
          ? null
          : {
              level: DimensionRowLevel.ROW_4,
              pls: colonnadePolyLines,
              halfEdges: polygonHalfEdgesToPolygonEdgeData(levelId, colonnadeDataByEdge),
            },
        {
          level: DimensionRowLevel.ROW_4,
          pls: exteriorWallDetails,
          witnessLineGap: exteriorOpeningTagOffset + WITNESS_LINE_GAP,
          halfEdges: polygonHalfEdgesToPolygonEdgeData(levelId, exteriorWallDetailDataByEdge),
        },
        { level: DimensionRowLevel.OBSTACLE, pls: porchObstacles },
        { level: DimensionRowLevel.OBSTACLE, pls: exteriorWallObstacles },
      ].filter((v) => v !== null),
      mergeRows3And4,
      baseLineOffsetMultiplier: exteriorDimensionOffsetMultiplier,
    });
  }

  if (!includePorchObstaclesAndColonadeData && (building.floorPlates[levelId]?.perimeterPolyLine2s ?? []).length > 0) {
    makeExteriorDimensionRows({
      output: dims,
      input: [
        {
          level: DimensionRowLevel.ROW_2,
          pls: perimeters,
          projectOnAxis: true,
          halfEdges: polygonHalfEdgesToPolygonEdgeData(levelId, perimeterWallDataByEdge),
        },
        {
          level: DimensionRowLevel.ROW_3,
          pls: perimeterDetails,
          halfEdges: polygonHalfEdgesToPolygonEdgeData(levelId, exteriorWallDataByEdge),
        },
        { level: DimensionRowLevel.ROW_4, pls: colonnadePolyLines },
        {
          level: DimensionRowLevel.ROW_4,
          pls: exteriorWallDetails,
          witnessLineGap: exteriorOpeningTagOffset + WITNESS_LINE_GAP,
          halfEdges: polygonHalfEdgesToPolygonEdgeData(levelId, exteriorWallDetailDataByEdge),
        },
        { level: DimensionRowLevel.OBSTACLE, pls: exteriorWallObstacles },
      ],
      mergeRows3And4,
      baseLineOffsetMultiplier: exteriorDimensionOffsetMultiplier,
    });
  }

  // assign provenance-based auto dimension handles if feature flag is enabled
  return generatedExteriorDimensionHydrateDocEntityHandle(dims, building, levelId, memberLevelIds);
}

function initializeEmptyWallDetailData(segments: Float64Array[]) {
  const wallData: number[][] = [];
  for (const segment of segments) {
    wallData.push(new Array(segment.length).fill(undefined));
  }
  return wallData;
}

/**
 * Assigns a member level id to composite level half edge data to allow mapping to
 * a room edge.
 */
function resolveCompositeHalfEdgeDataToMemberLevels(
  halfEdgeDataList: HalfEdgeData[],
  memberLevelIds: string[],
  building: Pick<Building, "planarGraphs" | "roomsByLevel" | "rooms">,
): HalfEdgeData[] {
  return halfEdgeDataList.map((halfEdgeData): HalfEdgeData => {
    if (building.planarGraphs[halfEdgeData.levelId]) {
      return halfEdgeData;
    }
    if (halfEdgeData.halfEdge === undefined) {
      return halfEdgeData;
    }

    const oppHalfEdge = oppositeHalfEdgeIndex(halfEdgeData.halfEdge);
    const halfEdgeMemberLevelId = memberLevelIds.find((memberLevelId) => {
      const graph = building.planarGraphs[memberLevelId];
      const roomIds = building.roomsByLevel[memberLevelId];
      if (!graph || !roomIds) {
        return false;
      }
      try {
        const ref = graph.halfEdgeIndexToPolygonEdgeRef(oppHalfEdge);
        return ref && ref.index !== undefined && roomIds[ref.index] !== undefined;
      } catch {
        // halfEdgeIndexToPolygonEdgeRef will throw a null index exception if the halfEdge
        // does not exist for the level. This error does not need to be reported, as it is expected
        // to fail for levels that the half edge is not included in.
        return false;
      }
    });

    if (!halfEdgeMemberLevelId) {
      return halfEdgeData;
    }

    return {
      ...halfEdgeData,
      levelId: halfEdgeMemberLevelId,
    };
  });
}

function generatedExteriorDimensionHydrateDocEntityHandle(
  dims: DimensionRowOutput[],
  building: Pick<Building, "mirror" | "planarGraphs" | "roomsByLevel" | "settings" | "rooms">,
  levelId: string,
  memberLevelIds: string[] = [],
): GeneratedDimensionMap {
  const criteria = makeProvenanceCriteria(building.settings, AutoExteriorDimensionProvenanceCriteria);
  let hydratedExteriorDimensions: GeneratedDimensionMap = {};

  dims.forEach((dim) => {
    const { halfEdges, dimLine, rowLevel } = dim;

    const { halfEdgeData } = collectDefinedHalfEdges(halfEdges);

    const dirMatch = getMatchingEdgeOrientation(dimLine, building.mirror);
    const isOrthogonal = dirMatch !== undefined;

    // if there are half edges, use the room edges to generate the handle
    if (halfEdges?.length && rowLevel !== undefined) {
      const resolvedHalfEdgeData =
        memberLevelIds.length > 0
          ? resolveCompositeHalfEdgeDataToMemberLevels(halfEdgeData, memberLevelIds, building)
          : halfEdgeData;
      const handle = autoDimensionFloorPlanExteriorByRoomHandleMultipleNew(
        criteria,
        levelId,
        getRoomEdgesFromHalfEdgeData(
          resolvedHalfEdgeData,
          building.planarGraphs,
          building.roomsByLevel,
          building?.rooms ?? {},
        ),
        rowLevel,
        isOrthogonal,
      );
      const handleHash = docEntityHandleHash(handle);
      if (handleHash.length !== 0) {
        hydratedExteriorDimensions = multimapAdd(
          hydratedExteriorDimensions,
          handleHash,
          generatedExteriorDimensionNew({
            ...dim,
            docEntityHandle: handle,
          }),
        );
        return;
      }
    }

    if (dirMatch === undefined || rowLevel === undefined) {
      // this won't be selectable and will receive the default level handle
      const handle = { type: "LEVEL", id: levelId } as LevelHandle;
      const handleHash = docEntityHandleHash(handle);
      if (handleHash.length !== 0) {
        hydratedExteriorDimensions = multimapAdd(
          hydratedExteriorDimensions,
          handleHash,
          generatedExteriorDimensionNew({ ...dim, docEntityHandle: handle }),
        );
        return;
      }
    } else {
      const handle = autoDimensionFloorPlanExteriorByOrientationHandleNew(criteria, levelId, dirMatch, rowLevel);
      const handleHash = docEntityHandleHash(handle);

      if (handleHash.length !== 0) {
        hydratedExteriorDimensions = multimapAdd(
          hydratedExteriorDimensions,
          handleHash,
          generatedExteriorDimensionNew({ ...dim, docEntityHandle: handle }),
        );
        return;
      }
    }
  });
  return hydratedExteriorDimensions;
}

function makeExteriorDimensionObstacles(
  building: Pick<Building, "planarGraphs" | "walls">,
  levelId: string,
): PolyLine2[] {
  const exteriorWallSegments: PolyLine2[] = [];

  const { planarGraphs, walls } = building;

  const levelGraph = planarGraphs[levelId].planarGraph;
  const levelWalls = walls[levelId];

  for (let i = 0; i < levelGraph.halfEdges.length; i += 2) {
    const halfEdgeIndex = i / 2;
    const wall = levelWalls.walls[levelWalls.halfEdgeToWall[halfEdgeIndex]];
    if (wall.wallType !== WallType.SOLID) {
      continue;
    }
    const { leftRoom, rightRoom } = getWallPath(wall);
    const { leftEdge, rightEdge } = solidWallSubstrateLayer(wall);
    if (!leftRoom || !isWalledRoomEnclosure[leftRoom.attributes.roomEnclosure]) {
      exteriorWallSegments.push(rightEdge);
      continue;
    }
    if (!rightRoom || !isWalledRoomEnclosure[rightRoom.attributes.roomEnclosure]) {
      exteriorWallSegments.push(leftEdge);
      continue;
    }

    if (
      isExteriorRoomEnclosure[leftRoom.attributes.roomEnclosure] &&
      isExteriorRoomEnclosure[rightRoom.attributes.roomEnclosure]
    ) {
      exteriorWallSegments.push(leftEdge);
      exteriorWallSegments.push(rightEdge);
    }
  }

  return exteriorWallSegments;
}
